// src/pages/Signup.js
import React, { useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import styled from "styled-components";
import Layout from "../central/Layout";
import Panel from "../central/Panel";
import Button from "../central/Button";

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: calc(100vh - 80px);
  background-color: ${({ theme }) => theme.colors.background};
  padding-top: ${({ theme }) => theme.spacing.xxlarge};
`;

const FormPanel = styled(Panel)`
  width: 100%;
  max-width: 420px;
  padding: ${({ theme }) => theme.spacing.xlarge};
`;

const Title = styled.h2`
  text-align: center;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  margin-bottom: ${({ theme }) => theme.spacing.large};
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`;

const Input = styled.input`
  padding: ${({ theme }) => theme.spacing.small};
  border-radius: ${({ theme }) => theme.radii.small};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-family: ${({ theme }) => theme.typography.fontFamily.body};
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  transition: border-color ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}33;
  }
`;

const Signup = () => {
  const managerNameRef = useRef();
  const emailRef = useRef();
  const passwordRef = useRef();
  const { signup } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const userCred = await signup(
        emailRef.current.value,
        passwordRef.current.value,
        managerNameRef.current.value
      );
      const user = userCred.user;
      console.log("✅ Auth user created:", user.uid);

      localStorage.setItem(
        "user",
        JSON.stringify({
          uid: user.uid,
          email: user.email,
          displayName: managerNameRef.current.value,
        })
      );

      try {
        const functions = getFunctions(undefined, "us-central1");

        console.log("📧 About to call sendWelcomeEmail for:", user.email);
        const sendWelcomeEmail = httpsCallable(functions, "sendWelcomeEmail");
        const welcomeResult = await sendWelcomeEmail({ email: user.email });
        console.log("✅ sendWelcomeEmail result:", welcomeResult);

        console.log("📧 About to call sendVerificationEmail for:", user.email);
        const sendVerificationEmail = httpsCallable(
          functions,
          "sendVerificationEmail"
        );
        const verificationResult = await sendVerificationEmail({
          email: user.email,
        });
        console.log("✅ sendVerificationEmail result:", verificationResult);
      } catch (emailErr) {
        console.error("❌ Signup email flow error:", emailErr);
      }

      navigate("/home");
    } catch (err) {
      console.error("❌ Signup error details:", err);
      setError(err.message || "Failed to create an account");
    }

    setLoading(false);
  };

  return (
    <Layout>
      <Container>
        <FormPanel>
          <Title>Sign Up</Title>
          {error && <ErrorText>{error}</ErrorText>}
          <StyledForm onSubmit={handleSubmit}>
            <Input type="text" ref={managerNameRef} placeholder="Real Name" required />
            <Input type="email" ref={emailRef} placeholder="Email" required />
            <Input type="password" ref={passwordRef} placeholder="Password" required />
            <Button type="submit" disabled={loading} variant="pink">
              {loading ? "Creating..." : "Sign Up"}
            </Button>
            <div style={{ textAlign: "center", marginTop: "1rem" }}>
              <p style={{ color: "#333", marginBottom: "0.5rem" }}>Already have an account?</p>
              <Button
                type="button"
                onClick={() => navigate("/login")}
                variant="blue"
                style={{ width: "100%" }}
              >
                Sign In
              </Button>
            </div>
          </StyledForm>
        </FormPanel>
      </Container>
    </Layout>
  );
};

export default Signup;